import { useState } from 'react';
import { useApp } from '../store';
import { can } from '../permissions';
import { assistantsOf, assistantName, setupProgress } from '../assistant';
import { relativeTime } from '../utils';
import AssistantPage from './AssistantPage';
import { RequestToAdd } from './RequestAccess';

/* Re-exported so nothing that already imports it from here has to move. The
   account form lives beside the page that uses it. */
export { AssistantAccount } from './AssistantPage';

/**
 * WHICH ASSISTANT — and nothing else.
 *
 * WHY THIS IS NOT A LIST OF PRODUCTS
 *
 * An earlier version handed over one command: the npm script that ships with
 * this repository. That is setup for one particular toolchain, not for a
 * customer, and it quietly says the software works with one assistant and
 * everything else is somebody's problem. There is no list of supported
 * assistants here and there should not be — naming them would date immediately,
 * would leave every unnamed one looking unsupported, and would put a vendor's
 * product name in software sold to people using something else.
 *
 * WHY THIS SCREEN IS NOW ONLY A LIST
 *
 * It used to be a list AND a four-step wizard, and the wizard was rendered only
 * while `assistants.length === 0 || adding`. So pressing "Add assistant" made
 * the count 1, set `adding` false, and unmounted steps two, three and four in
 * the instant they became relevant — on the first assistant, which is the only
 * path anybody new ever takes. The account was never issued, the instructions
 * never shown, and the row left behind offered a switch and a Remove button.
 *
 * A list answers "which one". The moment that is answered it is in the way, and
 * everything else belongs on the assistant's own page — where it stays editable
 * rather than being a wizard somebody has to walk again to change one setting.
 * Same shape as the customer page in the operator portal, for the same reason.
 */
export default function AssistantSetup() {
  const meta = useApp((s) => s.meta);
  const role = useApp((s) => s.shownRole);
  const setMeta = useApp((s) => s.setMeta);
  const canSettings = can(role, 'editSettings');
  /* Any member of the board may ASK for an assistant — #359, widened. They use
     an assistant to help run the board, so the floor is the same rank the row
     policy accepts (member), not manager. Minting it is still admin-only. */
  const canAsk = can(role, 'askToAdd');
  const assistants = assistantsOf(meta);
  /* Minting an assistant's account is inviting, which is admin-only since #359.
     A board member who is not a workspace admin ASKS instead, and finishes the
     approved one here — the panel carries both. `s.role` is the WORKSPACE rank,
     which is what admin-ness is; `shownRole` above is only this board's. */
  const amWorkspaceAdmin = useApp((s) => s.role === 'admin');
  const projectId = useApp((s) => s.projectId);

  /** Which one is open. Empty means the list. */
  const [open, setOpen] = useState('');
  const [adding, setAdding] = useState(false);
  const [chosenName, setChosenName] = useState('');

  const id =
    (chosenName.trim() || 'my-assistant')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'my-assistant';

  /* Adding the same name twice would silently replace the existing entry, and
     with it whatever permission had been granted. */
  const alreadyNamed = Boolean(meta.assistants?.[id]);

  if (open && meta.assistants?.[open]) {
    return <AssistantPage id={open} onBack={() => setOpen('')} />;
  }

  /*
   * ADDING GOES STRAIGHT ONTO THE PAGE, and this is the whole fix.
   *
   * The old handler wrote the entry, cleared the name box and set `adding`
   * false — three things, of which two were wrong. Clearing the box orphaned
   * the later steps from the assistant just added, so they would have built an
   * account for "my-assistant" rather than the one named. And closing the panel
   * was what made steps two to four vanish.
   *
   * Here the entry is written and the page for it is opened in the same press.
   * There is no state in which an assistant exists and there is nowhere to
   * finish setting it up, because the page IS where you land.
   */
  const add = () => {
    const named = chosenName.trim();
    if (!named || alreadyNamed) return;
    setMeta({
      assistants: {
        ...(meta.assistants ?? {}),
        [id]: { name: named, addedAt: Date.now() },
      },
    });
    setChosenName('');
    setAdding(false);
    setOpen(id);
  };

  return (
    <>
    {/* Not a workspace admin, but on this board — ask for the account, and
        finish the approved one here. Any member and up may ask (they run the
        board with it); a viewer or reviewer sees nothing to add, which is also
        what the RLS behind the request would tell them. */}
    {!amWorkspaceAdmin && canAsk && projectId && <RequestToAdd kind="assistant" boardId={projectId} />}
    <section className="panel">
      <h2>Assistants</h2>
      <p className="hint">
        An assistant can read what is written on a card and answer there, so a question asked on an
        issue is answered on that issue rather than somewhere else. Each one is switched on
        separately, and off until you say otherwise.
      </p>

      {assistants.length === 0 && !adding && (
        <div className="asst-empty">
          <strong>Nothing is connected yet.</strong>
          <p className="hint">
            Adding one takes three or four short steps, and the app does most of them. Nothing needs
            installing and no repository needs cloning — you give it a name, say who puts the
            tracker on the board, and paste one block of instructions into your assistant.
          </p>
        </div>
      )}

      {assistants.length > 0 && (
        <div className="bl-rows asst-rows">
          {assistants.map((a) => {
            const progress = setupProgress(a);
            const on = a.mayAnswer === true;
            return (
              <article
                key={a.id}
                className="audit-row asst-open"
                role="button"
                tabIndex={0}
                onClick={() => setOpen(a.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpen(a.id);
                  }
                }}
              >
                <div className="audit-main">
                  <div className="audit-line">
                    <span className="audit-who">{assistantName(a, a.id)}</span>
                    {/*
                      SAID ON THE LIST, so a half-finished one cannot sit there
                      looking connected. This is the state Vinnie was left in —
                      an assistant added, nothing else done, and no sign of it.
                    */}
                    {!progress.finished && <span className="asst-tag asst-tag-todo">setup unfinished</span>}
                    {on && <span className="asst-tag asst-tag-on">answering</span>}
                  </div>
                  <div className="audit-sub">
                    {progress.finished
                      ? a.lastSeen
                        ? `Last worked here ${relativeTime(a.lastSeen)}.`
                        : 'Set up.'
                      : progress.todo}
                    {a.email ? ` Signs in as ${a.email}.` : ''}
                  </div>
                </div>
                <span className="btn btn-small">Open</span>
              </article>
            );
          })}
        </div>
      )}

      {adding ? (
        <div className="card add-card">
          <h3>Add an assistant</h3>
          <label className="stack">
            Its name
            <input
              className="input"
              value={chosenName}
              placeholder="My assistant"
              autoFocus
              disabled={!canSettings}
              onChange={(e) => setChosenName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
              }}
            />
            <span className="hint">
              The name you will see in the list. Only you see it — the assistant is never told its
              name. It is added switched off, and stays off until you turn it on.
            </span>
          </label>
          {alreadyNamed && (
            <p className="field-error">
              There is already an assistant called that. Give this one a different name, or open the
              existing one from the list.
            </p>
          )}
          <p className="hint">
            Nothing happens to your board when you press this. It creates a name to hang the rest of
            the setup on, and takes you to its page to finish.
          </p>
          <div className="btn-row">
            <button
              className="btn btn-primary"
              disabled={!canSettings || !chosenName.trim() || alreadyNamed}
              onClick={add}
            >
              Add it and continue
            </button>
            <button
              className="btn"
              onClick={() => {
                setAdding(false);
                setChosenName('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        canSettings && (
          <button className="btn btn-small" onClick={() => setAdding(true)}>
            {assistants.length === 0 ? 'Add an assistant' : 'Add another assistant'}
          </button>
        )
      )}

      <p className="hint">
        Adding one here does not connect anything. FlightDeck never contacts your assistant — your
        assistant checks these settings itself before it does anything, so turning one off actually
        stops it rather than just hiding a button.
      </p>
      <p className="hint">
        Anything an assistant writes appears under its own name, never under a person&apos;s, so
        nobody has to wonder who wrote something. If a comment asks for something to be{' '}
        <em>changed</em>, that still comes back to a person before anything is altered.
      </p>
    </section>
    </>
  );
}
